// Shared chrome for a dashboard shown as a SCREEN — the Run view on the
// platform (DashboardPreviewModal's PreviewBody) and in the downloaded runtime
// (VisualEmbed `asScreen`), so both draw the same header, filter card, drill
// trail and tile frame. The data, drill and quick-filter logic stay with each
// host; this file is presentation only.
//
// THEME COLOUR. A dashboard's DASHBOARD.THEME_COLOR is 'default' (absent ⇒
// 'default'), one of the ids in dashboardThemeColors.json, or a '#rrggbb'
// custom colour. 'default' is the APPLICATION's selected colour — the Run
// shell publishes it as --app-theme-color, so it is read from the page rather
// than threaded through every host. `useDashboardAccent` turns the choice into
// CSS variables on the dashboard root: --dash-* for this chrome, and the
// platform accent tokens (--color-accent, --pg-primary, --app-theme-color…)
// re-pointed for the shared controls inside (criteria form, quick-filter
// toggle and badges, primary buttons). Text and button colours are adjusted
// until they read at 4.5:1 against the card surface, in light and dark theme.
// A visual's own chart colours are the visual's design and are not touched.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useHostNamesObject, useScreenMargin } from '@twasta/page-runtime/txn'
import THEME_DATA from './dashboardThemeColors.json'
import { WHITE, BLACK, contrast, expandHex, luminance, mix, parseColor, readableOn, toHex } from './color'

export interface DashboardThemeColor {
  id: string
  label: string
  /** Absent for 'default' — that one is the application's colour. */
  color?: string
}

export const DASHBOARD_THEME_COLORS: DashboardThemeColor[] = THEME_DATA.colors
export const DASHBOARD_THEME_DEFAULT: string = THEME_DATA.default

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

/** The stored form of a THEME_COLOR: 'default', a known id, or a lower-case
 *  '#rrggbb'. Anything else is 'default'. */
export function normalizeDashboardThemeColor(v: unknown): string {
  const s = typeof v === 'string' ? v.trim() : ''
  if (!s) return DASHBOARD_THEME_DEFAULT
  const id = s.toLowerCase()
  if (DASHBOARD_THEME_COLORS.some((c) => c.id === id)) return id
  if (HEX.test(s)) return expandHex(s)
  return DASHBOARD_THEME_DEFAULT
}

/** The colour a THEME_COLOR names, or null for 'default' (the application's). */
export function dashboardThemeColorHex(v: unknown): string | null {
  const n = normalizeDashboardThemeColor(v)
  if (n.startsWith('#')) return n
  return DASHBOARD_THEME_COLORS.find((c) => c.id === n)?.color ?? null
}

/** CSS variables for a dashboard root wearing `accent` on `surface` (the card
 *  colour). Empty when the accent cannot be read — the host's accent stays. */
export function dashboardAccentVars(accent: string | null, surface: string | null): CSSProperties {
  const a = parseColor(accent)
  if (!a) return {}
  const bg = parseColor(surface) ?? WHITE
  const dark = luminance(bg) < 0.4
  const ink = readableOn(a, bg)
  const inkHex = toHex(ink)
  const onInk = contrast(ink, WHITE) >= 4.5 ? '#ffffff' : '#111827'
  return {
    '--dash-accent': toHex(a),
    '--dash-ink': inkHex,
    '--dash-on-ink': onInk,
    '--dash-weak': toHex(mix(a, bg, dark ? 0.8 : 0.9)),
    '--dash-weak-border': toHex(mix(a, bg, 0.6)),
    // The shared controls inside this dashboard read the platform tokens.
    '--color-accent': inkHex,
    '--color-accent-hvr': toHex(mix(ink, dark ? WHITE : BLACK, 0.12)),
    '--color-accent-weak': `rgba(${a.map(Math.round).join(', ')}, ${dark ? 0.2 : 0.12})`,
    '--color-primary': inkHex,
    '--pg-primary': inkHex,
    '--app-theme-color': inkHex,
  } as CSSProperties
}

/**
 * The dashboard root's accent. `themeColor` is the model's THEME_COLOR;
 * `appColor`, when a host knows it, is the application's colour ('default').
 * Without one, 'default' reads the application's colour the Run shell put on
 * the page (--app-theme-color), else the platform accent. Spread `style` on
 * the element `ref` points at.
 */
export function useDashboardAccent(themeColor: unknown, appColor?: string | null) {
  const ref = useRef<HTMLDivElement | null>(null)
  const explicit = dashboardThemeColorHex(themeColor)
  const given = appColor && parseColor(appColor) ? appColor : null
  const [env, setEnv] = useState<{ app: string | null; surface: string | null }>({ app: null, surface: null })

  const read = () => {
    const el = ref.current?.parentElement
    if (!el || typeof getComputedStyle !== 'function') return
    const cs = getComputedStyle(el)
    const app = cs.getPropertyValue('--app-theme-color').trim()
      || cs.getPropertyValue('--color-accent').trim() || null
    const surface = cs.getPropertyValue('--color-bg-secondary').trim() || null
    setEnv((p) => (p.app === app && p.surface === surface ? p : { app, surface }))
  }
  // Before paint, so the dashboard never flashes in the platform blue.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(read, [explicit, given])
  // A theme switch (body class) or the shell publishing the application's
  // colour (html style) after this mounted — read again.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return undefined
    const mo = new MutationObserver(read)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] })
    if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] })
    return () => mo.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const accent = explicit || given || env.app
  const style = useMemo(() => dashboardAccentVars(accent, env.surface), [accent, env.surface])
  return { ref, style }
}

// ── Layout constants shared by both screen hosts ──────────────────────────

/** Grid geometry of a dashboard screen: row unit, gap between tiles. */
export const DASH_SCREEN_ROW_PX = 80
export const DASH_SCREEN_GAP = 16

/**
 * Page around the header, filter card and grid. Its MARGIN is not here: it is
 * the one rule every screen follows (the transaction list included) —
 * `useDashScreenMargin` below, which leaves none inside the Run shell (the
 * shell's frame is the margin) and a small one elsewhere.
 */
export const dashScreenPageStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 16,
  boxSizing: 'border-box', minWidth: 0,
  background: 'var(--color-bg)', color: 'var(--color-text)',
  fontFamily: 'var(--font-primary)',
}

/** The margin round a dashboard / visual screen showing `objName` — the same
 *  rule as the transaction list (`screenMargin` in page-runtime). `overlay` =
 *  the screen's own full-screen overlay. */
export function useDashScreenMargin(objName: string | undefined, objType: 'D' | 'V',
  overlay = false): string {
  return useScreenMargin(objName || '', objType, { overlay })
}

/**
 * Whether a dashboard / visual screen draws its own NAME in its header — the
 * transaction list's rule: not when the surrounding chrome (the Run shell's
 * screen title) already names this object, which would show it twice. It
 * still does in its own full-screen overlay (that covers the shell's title)
 * and on a drilled level (a different thing from the one the shell names).
 * The description, the "Updated / rows" line and the buttons always stay.
 */
export function useDashScreenShowsTitle(objName: string | undefined, objType: 'D' | 'V',
  { overlay = false, drilled = false }: { overlay?: boolean; drilled?: boolean } = {}): boolean {
  const hosted = useHostNamesObject(objName || '', objType)
  return !hosted || overlay || drilled
}

// ── Small inline icons (this package ships no icon library) ────────────────

function Svg({ size = 16, children }: { size?: number; children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  )
}

export function DashIconRefresh({ size = 14 }: { size?: number }) {
  return <Svg size={size}><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /></Svg>
}
export function DashIconChevronLeft({ size = 16 }: { size?: number }) {
  return <Svg size={size}><path d="m15 18-6-6 6-6" /></Svg>
}
export function DashIconChevronRight({ size = 14 }: { size?: number }) {
  return <Svg size={size}><path d="m9 18 6-6-6-6" /></Svg>
}
export function DashIconMaximize({ size = 16 }: { size?: number }) {
  return <Svg size={size}><path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" /></Svg>
}
export function DashIconMinimize({ size = 16 }: { size?: number }) {
  return <Svg size={size}><path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" /></Svg>
}
export function DashIconPlay({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor"
      strokeWidth={2} strokeLinejoin="round" aria-hidden="true">
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  )
}

// ── Buttons ────────────────────────────────────────────────────────────────

/** A 36px square icon button for the header's action row. */
export function DashboardIconButton({ label, onClick, children, pressed }: {
  label: string
  onClick: () => void
  children: ReactNode
  pressed?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      style={{
        width: 36, height: 36, flexShrink: 0, borderRadius: 8, cursor: 'pointer',
        border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
        color: 'var(--color-text-secondary)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {children}
    </button>
  )
}

/** The filter card's primary Apply button, in the dashboard's colour. */
export function dashApplyButtonStyle(disabled?: boolean): CSSProperties {
  return {
    height: 34, display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
    padding: '0 16px', borderRadius: 8, border: 'none',
    background: 'var(--dash-ink, var(--color-accent))', backgroundImage: 'none',
    color: 'var(--dash-on-ink, #ffffff)', fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
  }
}

// ── Header ─────────────────────────────────────────────────────────────────

function refreshText(seconds: number): string {
  if (seconds < 60) return `${seconds} s`
  return `${Math.round(seconds / 60)} min`
}

export interface DashboardHeaderProps {
  title?: string | null
  /** The dashboard's description. Blank ⇒ the line is not rendered at all and
   *  the meta row closes up under the title. */
  description?: string | null
  /** When the tiles were last fetched (shown as "Updated hh:mm"). */
  updatedAt?: Date | null
  /** REFRESH_INTERVAL_SECONDS — shown only when auto-refresh is on. */
  refreshSeconds?: number
  tileCount?: number
  /** A count of anything else the screen shows ("1,248 rows" on a single
   *  visual), in the same meta line. */
  countText?: string | null
  /** Right-hand action row (quick filter, full screen, the run log…). */
  actions?: ReactNode
}

export function DashboardHeader({
  title, description, updatedAt, refreshSeconds = 0, tileCount, countText, actions,
}: DashboardHeaderProps) {
  const desc = (description || '').trim()
  const meta: ReactNode[] = []
  if (updatedAt) {
    meta.push(
      <span key="u" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <DashIconRefresh size={13} />
        Updated {updatedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </span>,
    )
  }
  if (refreshSeconds > 0) {
    meta.push(
      <span key="r" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-success)' }} />
        Auto-refresh every {refreshText(refreshSeconds)}
      </span>,
    )
  }
  if (typeof tileCount === 'number') {
    meta.push(<span key="t">{tileCount} {tileCount === 1 ? 'tile' : 'tiles'}</span>)
  }
  if (countText) meta.push(<span key="c">{countText}</span>)
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px 24px' }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        {title && (
          <h1 style={{
            margin: 0, fontSize: 24, lineHeight: 1.25, fontWeight: 600, letterSpacing: '-0.01em',
            color: 'var(--color-text)', overflowWrap: 'anywhere',
          }}>
            {title}
          </h1>
        )}
        {desc && (
          <p style={{
            margin: '6px 0 0', maxWidth: 760, fontSize: 15, lineHeight: 1.5,
            color: 'var(--color-text-secondary)', whiteSpace: 'pre-line',
          }}>
            {desc}
          </p>
        )}
        {meta.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px',
            marginTop: title || desc ? (desc ? 8 : 4) : 0,
            fontSize: 13, color: 'var(--color-text-faint, var(--color-text-secondary))',
          }}>
            {meta}
          </div>
        )}
      </div>
      {actions && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {actions}
        </div>
      )}
    </div>
  )
}

// ── Filter card ────────────────────────────────────────────────────────────

/** The global-criteria band as one card, pinned while the tiles scroll. Holds
 *  the shared CriteriaBar (look="card"). No overflow clipping — a lookup
 *  picker inside must be free to open past the card.
 *
 *  `band` — a single visual's screen: a flat control band (the contrast
 *  surface, no shadow) that scrolls with the page, so it reads as the controls
 *  OF the raised visual card under it and never floats over the visual's
 *  rows. */
export function DashboardFilterCard({ children, band = false }: { children: ReactNode; band?: boolean }) {
  return (
    <section
      aria-label="Filters"
      style={band ? {
        background: 'var(--color-surface-2, var(--color-bg-secondary))',
        border: '1px solid var(--color-border)', borderRadius: 12,
      } : {
        position: 'sticky', top: 0, zIndex: 20,
        background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
        borderRadius: 12, boxShadow: '0 1px 2px var(--color-shadow), 0 4px 12px rgba(28,36,48,0.04)',
      }}
    >
      {children}
    </section>
  )
}

/** The quick-filter banner, as its own rounded band under the filter card. */
export function DashboardQuickFilterBand({ children }: { children: ReactNode }) {
  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--dash-weak-border, var(--color-border))' }}>
      {children}
    </div>
  )
}

// ── Drill trail ────────────────────────────────────────────────────────────

/** Back + every level as a link; the current level is plain bold text. */
export function DashboardDrillTrail({ labels, onBack, onGo, note }: {
  labels: string[]
  onBack: () => void
  onGo: (index: number) => void
  /** What opened the current level (e.g. "Partner = Melody House"). */
  note?: ReactNode
}) {
  const last = labels.length - 1
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px',
      padding: '8px 12px', borderRadius: 12, border: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)',
    }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          height: 32, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 12px 0 8px',
          borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
          color: 'var(--color-text)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
        }}
      >
        <DashIconChevronLeft /> Back
      </button>
      <nav aria-label="Drill path" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 14, minWidth: 0 }}>
        {labels.map((label, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {i > 0 && <span style={{ color: 'var(--color-text-faint, var(--color-text-secondary))', display: 'inline-flex' }}><DashIconChevronRight /></span>}
            {i === last ? (
              <span aria-current="page" style={{ fontWeight: 600, color: 'var(--color-text)' }}>{label}</span>
            ) : (
              <button
                type="button"
                onClick={() => onGo(i)}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--dash-ink, var(--color-accent))', fontWeight: 500, fontSize: 14,
                }}
              >
                {label}
              </button>
            )}
          </span>
        ))}
      </nav>
      {note && (
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--color-text-secondary)' }}>{note}</span>
      )}
    </div>
  )
}

// ── Tile frame ─────────────────────────────────────────────────────────────

export type DashboardTileTone = 'normal' | 'quick' | 'warn'

/** "Click to filter" (a 'self' cross-filter) / "Click to drill" (another
 *  dashboard or visual) — a tile whose clicks do something says so. */
export function DashboardDrillHint({ kind }: { kind: 'filter' | 'drill' }) {
  const text = kind === 'filter' ? 'Click to filter' : 'Click to drill'
  return (
    <span
      title={kind === 'filter' ? 'Click a row or point to filter this dashboard by it' : 'Click a row or point to drill through'}
      style={{
        height: 24, display: 'inline-flex', alignItems: 'center', gap: 2, padding: '0 8px', flexShrink: 0,
        borderRadius: 12, border: '1px solid var(--dash-weak-border, var(--color-border))',
        color: 'var(--dash-ink, var(--color-accent))', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      {text} <DashIconChevronRight size={12} />
    </span>
  )
}

export interface DashboardTileFrameProps {
  /** Grid placement (dashTileStyle). */
  placement: CSSProperties
  title: string
  /** SHOW_HEADER — off renders the body full-bleed; `floating` then carries
   *  the badges over the visual. */
  showHeader: boolean
  tone?: DashboardTileTone
  /** Header items after the title (drill hint, filter badge). */
  headerExtras?: ReactNode
  /** Badges for a header-less tile, floated over its top-left corner. */
  floating?: ReactNode
  /** Extra attributes for the root (test hooks). */
  attrs?: Record<string, unknown>
  /** The body; receives the header's menu slot so the visual engine can mount
   *  its ⋮ menu there (null when the header is hidden). */
  children: (menuHost: HTMLElement | null) => ReactNode
}

export function DashboardTileFrame({
  placement, title, showHeader, tone = 'normal', headerExtras, floating, attrs, children,
}: DashboardTileFrameProps) {
  const [menuHost, setMenuHost] = useState<HTMLElement | null>(null)
  const border = tone === 'warn'
    ? '1px solid var(--color-warning)'
    : tone === 'quick'
      ? '1px dashed var(--dash-ink, var(--color-accent))'
      : '1px solid var(--color-border)'
  return (
    <div
      {...(attrs || {})}
      style={{
        ...placement,
        border, borderRadius: 12, background: 'var(--color-bg-secondary)',
        boxShadow: '0 1px 2px var(--color-shadow)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative',
      }}
    >
      {showHeader && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, minHeight: 32,
          padding: '10px 8px 2px 16px', flexShrink: 0,
        }}>
          <h2 style={{
            margin: 0, flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.35,
            color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={title}>
            {title}
          </h2>
          {headerExtras}
          {/* ⋮ menu slot — the visual engine mounts its chart menu here. */}
          <span ref={setMenuHost} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }} />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', margin: showHeader ? '0 4px 4px' : 0 }}>
        {!showHeader && floating && (
          <span style={{ position: 'absolute', top: 6, left: 8, zIndex: 3 }}>{floating}</span>
        )}
        {children(showHeader ? menuHost : null)}
      </div>
    </div>
  )
}

/** Centred status text inside a tile body (loading / error / no data). */
export function DashboardTileMessage({ children, tone }: { children: ReactNode; tone?: 'error' | 'muted' }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, padding: 16, textAlign: 'center', fontSize: 14,
      color: tone === 'error' ? 'var(--color-error)' : 'var(--color-text-secondary)',
    }}>
      {children}
    </div>
  )
}

// ── A single visual as a screen ────────────────────────────────────────────

/** "1,248 rows" / "first 500 rows" for a visual's header meta line. */
export function visualRowsText(rows: number | undefined, truncated?: boolean): string | null {
  if (rows === undefined) return null
  const n = rows.toLocaleString()
  if (truncated) return `First ${n} rows`
  return `${n} ${rows === 1 ? 'row' : 'rows'}`
}

export interface VisualScreenFrameProps {
  /** The screen's name — the menu entry that opened it. */
  title?: string | null
  description?: string | null
  updatedAt?: Date | null
  refreshSeconds?: number
  /** visualRowsText(...) of the last run. */
  countText?: string | null
  /** Header action row (SQL, refresh, full screen). */
  actions?: ReactNode
  /** The criteria bar in its card look; omitted ⇒ no filter card. */
  filters?: ReactNode
  /** Panels inside the card above the visual (the author's SQL, diagnostics). */
  tools?: ReactNode
  /** The application's colour, when the host knows it (the builder). */
  appColor?: string | null
  /** Extra attributes for the root (test hooks). */
  attrs?: Record<string, unknown>
  /** The visual's object name — lets the frame ask whether the host already
   *  frames this screen (the Run shell), for the shared margin rule. */
  objName?: string
  /** The screen is in its own full-screen overlay (wider margin). */
  overlay?: boolean
  /** Root style overrides — a host sizes the screen to its pane. */
  style?: CSSProperties
  /** The visual, a status message, … — laid out to fill the card body. */
  children: ReactNode
}

/**
 * One visual opened as a screen, worn the way a dashboard is: the dashboard
 * header (name, description, freshness, row count), the criteria as the filter
 * card, and the visual in a single tile-style card that fills the rest of the
 * pane. Both screen hosts (the platform Run view / designer preview and the
 * downloaded runtime) draw a visual through this, so they cannot drift.
 */
export function VisualScreenFrame({
  title, description, updatedAt, refreshSeconds, countText, actions, filters, tools,
  appColor, attrs, objName, overlay = false, style, children,
}: VisualScreenFrameProps) {
  const { ref, style: accentStyle } = useDashboardAccent(null, appColor)
  const margin = useDashScreenMargin(objName, 'V', overlay)
  const showTitle = useDashScreenShowsTitle(objName, 'V', { overlay })
  return (
    <div
      ref={ref}
      {...(attrs || {})}
      style={{ ...accentStyle, ...dashScreenPageStyle, padding: margin, height: '100%', overflow: 'auto', ...style }}
    >
      <DashboardHeader
        title={showTitle ? title : null}
        description={description}
        updatedAt={updatedAt}
        refreshSeconds={refreshSeconds}
        countText={countText}
        actions={actions}
      />
      {filters && <DashboardFilterCard band>{filters}</DashboardFilterCard>}
      <section
        aria-label={title || 'Visual'}
        style={{
          flex: '1 0 auto', minHeight: 460, display: 'flex', flexDirection: 'column', minWidth: 0,
          // A little more air than the page's 16 px rhythm between the
          // controls above and the content they drive.
          marginTop: filters ? 4 : 0,
          border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-bg-secondary)',
          boxShadow: '0 1px 2px var(--color-shadow)', overflow: 'hidden',
        }}
      >
        {tools}
        {/* Absolute inner box: the visual gets a DEFINITE height to fill (a
            chart sizes to its container), and a taller native visual scrolls
            inside the card rather than pushing the page. */}
        <div style={{ flex: 1, minHeight: 420, position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 8, overflow: 'auto' }}>
            {children}
          </div>
        </div>
      </section>
    </div>
  )
}
